import React, { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, Loader2, CheckCircle2 } from "lucide-react";
import { api } from "../lib/api";
import { ChatPanel } from "../components/ChatPanel";

export default function LetsBuild() {
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState("");
  const [summary, setSummary] = useState("");
  const [summarizing, setSummarizing] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [greeting, setGreeting] = useState("Hi! Let's scope your project. Tell me — what are you trying to build or solve, and which areas are involved (support, migration, call center, software, security, etc.)?");

  useEffect(() => {
    api.get("/content").then((r) => { if (r.data?.build_greeting) setGreeting(r.data.build_greeting); }).catch(() => {});
  }, []);

  const handleMessages = useCallback((msgs, sid) => {
    setMessages(msgs);
    setSessionId(sid);
  }, []);

  const hasConversation = messages.filter((m) => m.role === "user").length > 0;

  const generateSummary = async () => {
    setSummarizing(true);
    setError("");
    try {
      const { data } = await api.post("/chat/summary", { session_id: sessionId });
      setSummary(data.summary || "");
    } catch (e) {
      setError("Could not generate a summary. You can still describe your project in the box below.");
    } finally {
      setSummarizing(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!summary.trim()) {
      setError("Please add a short project summary first.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads", {
        ...form,
        summary,
        source: "lets-build",
        transcript: messages,
      });
      setSubmitted(true);
    } catch (e) {
      setError("Something went wrong submitting your request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="mx-auto flex max-w-2xl flex-col items-center px-5 py-28 text-center sm:px-8">
        <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.4 }}>
          <CheckCircle2 className="h-16 w-16 text-klein" strokeWidth={1.2} />
        </motion.div>
        <h1 className="mt-6 font-heading text-3xl font-black tracking-tighter text-zinc-900 sm:text-4xl">
          Request received, {form.name.split(" ")[0] || "thanks"}!
        </h1>
        <p className="mt-4 max-w-md text-base leading-relaxed text-zinc-600">
          Thank you for sharing your project with us. Our team has your brief and <strong>will contact you shortly</strong> at {form.email} to move things forward.
        </p>
        <div className="mt-8 w-full border border-zinc-200 bg-zinc-50 p-6 text-left">
          <span className="font-mono-label text-[11px] text-zinc-400">Your project brief</span>
          <p className="mt-2 text-sm leading-relaxed text-zinc-700">{summary}</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <section className="border-b border-zinc-200 bg-zinc-50">
        <div className="mx-auto max-w-7xl px-5 py-14 sm:px-8 lg:py-16">
          <div className="inline-flex items-center gap-2 border border-zinc-200 bg-white px-3 py-1.5">
            <Sparkles className="h-3.5 w-3.5 text-accentred" />
            <span className="font-mono-label text-[11px] text-zinc-500">AI Project Consultant</span>
          </div>
          <h1 className="mt-5 max-w-3xl font-heading text-4xl font-black tracking-tighter text-zinc-900 sm:text-5xl">
            Let's build your project
          </h1>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-zinc-600">
            Chat with our AI consultant about what you need. It will scope your project, summarize the requirements, and our team will reach out to take it from there.
          </p>
        </div>
      </section>

      <section className="mx-auto grid max-w-7xl gap-8 px-5 py-12 sm:px-8 lg:grid-cols-12 lg:py-16">
        {/* Chat */}
        <div className="lg:col-span-7">
          <span className="font-mono-label text-[11px] text-klein">Step 1 · Describe your needs</span>
          <div className="mt-3">
            <ChatPanel
              mode="build"
              sessionPrefix="build"
              greeting={greeting}
              placeholder="Describe your project or goal..."
              heightClass="h-[440px]"
              onMessages={handleMessages}
            />
          </div>
        </div>

        {/* Summary + form */}
        <div className="lg:col-span-5">
          <span className="font-mono-label text-[11px] text-klein">Step 2 · Confirm & submit</span>
          <div className="mt-3 border border-zinc-200 p-6">
            <div className="flex items-center justify-between">
              <h3 className="font-heading text-lg font-semibold text-zinc-900">Project summary</h3>
              <button
                onClick={generateSummary}
                disabled={!hasConversation || summarizing}
                data-testid="generate-summary-btn"
                className="inline-flex items-center gap-1.5 border border-klein px-3 py-1.5 text-xs font-semibold text-klein transition-colors hover:bg-klein hover:text-white disabled:opacity-40"
              >
                {summarizing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                Summarize chat
              </button>
            </div>
            <textarea
              data-testid="summary-textarea"
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder="Your project summary will appear here after you chat with our consultant — or write it yourself."
              rows={5}
              className="mt-3 w-full resize-none border border-zinc-200 bg-white p-3 text-sm leading-relaxed outline-none focus:border-klein"
            />

            <form onSubmit={submit} className="mt-5 space-y-3">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <input data-testid="lead-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name *" className="border border-zinc-200 px-3 py-2.5 text-sm outline-none focus:border-klein" />
                <input data-testid="lead-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="Company" className="border border-zinc-200 px-3 py-2.5 text-sm outline-none focus:border-klein" />
              </div>
              <input data-testid="lead-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Work email *" className="w-full border border-zinc-200 px-3 py-2.5 text-sm outline-none focus:border-klein" />
              <input data-testid="lead-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone" className="w-full border border-zinc-200 px-3 py-2.5 text-sm outline-none focus:border-klein" />

              {error && <p data-testid="lead-error" className="text-sm text-accentred">{error}</p>}

              <button
                type="submit"
                data-testid="submit-lead-btn"
                disabled={submitting}
                className="flex w-full items-center justify-center gap-2 bg-klein px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-kleindark disabled:opacity-50"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Submit project request
              </button>
              <p className="text-center text-xs text-zinc-400">We'll review your brief and contact you shortly.</p>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
